import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import FinalCta from "../sections/FinalCta";
import { useLang } from "../i18n/LanguageContext";
import { PROJECTS_I18N } from "../i18n/content";

const ALL = "__all__";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(ALL);
  const { lang } = useLang();
  const t = PROJECTS_I18N[lang];

  useEffect(() => {
    api.get("/projects").then((r) => {
      const list = (r.data || []).filter((p) => p.list_visible !== false);
      list.sort((a, b) => {
        const ao = a.order ?? 999, bo = b.order ?? 999;
        if (ao !== bo) return ao - bo;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setItems(list);
    }).catch(() => {});
  }, []);

  const filters = useMemo(() => {
    const seen = [];
    for (const it of items) {
      const s = (it.sector || "").trim();
      if (s && !seen.some((x) => x.toLowerCase() === s.toLowerCase())) seen.push(s);
    }
    return seen;
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === ALL) return items;
    return items.filter((i) => (i.sector || "").trim().toLowerCase() === filter.toLowerCase());
  }, [items, filter]);

  return (
    <>
      <SEO title={t.seoTitle} description={t.seoDesc} />
      <PageHero eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />

      <section className="section bg-white">
        <div className="container-x">
          <div className="flex flex-wrap gap-2" data-testid="project-filters">
            {[ALL, ...filters].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`project-filter-${f === ALL ? "all" : f}`}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition ${
                  filter === f
                    ? "bg-[#07111F] border-[#07111F] text-white"
                    : "bg-white border-slate-200 text-[#334155] hover:border-[#2563EB]/40"
                }`}
              >
                {f === ALL ? t.all : f}
              </button>
            ))}
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((it, i) => (
              <motion.article
                key={it.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="card-elevate overflow-hidden"
                data-testid={`project-card-${it.slug}`}
              >
                {it.cover_image && (
                  <Link to={`/projeler/${it.slug}`} className="block aspect-[16/9] overflow-hidden bg-slate-100" data-testid={`project-image-link-${it.slug}`}>
                    <img src={it.cover_image} alt={it.title} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  </Link>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#2563EB]">{it.sector}</span>
                    {it.badge ? (
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                        it.project_type === "GERÇEK PROJE"
                          ? "bg-[#10B981]/10 text-[#0E9F6E]"
                          : "bg-[#22D3EE]/10 text-[#0E7490]"
                      }`} data-testid={`project-badge-${it.slug}`}>
                        {t.badges[it.badge] || it.badge}
                      </span>
                    ) : it.duration_days ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#22D3EE]/10 px-2.5 py-1 text-[11px] font-semibold text-[#0E7490]" data-testid={`project-duration-${it.slug}`}>
                        <Clock className="h-3 w-3" /> {t.duration(it.duration_days)}
                      </span>
                    ) : null}
                  </div>
                  <Link to={`/projeler/${it.slug}`} data-testid={`project-title-link-${it.slug}`} className="mt-2 block">
                    <h3 className="font-heading text-lg font-semibold text-[#07111F] leading-snug hover:text-[#2563EB] transition-colors">{it.title}</h3>
                  </Link>
                  <p className="mt-2 text-sm text-[#334155] leading-relaxed line-clamp-2">{it.summary || it.need}</p>
                  <Link to={`/projeler/${it.slug}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#2563EB] hover:gap-2 transition-all">
                    {t.explore} <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </motion.article>
            ))}
            {!filtered.length && <div className="text-sm text-[#334155]">{t.empty}</div>}
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
